import _ from "lodash";
/** external */
import React from "react";
import PropTypes from "prop-types";
import { Modal, Alert, Button } from "@toddle-edu/ds-web";
import { connect } from "react-redux";
/** internal */
import { useI18n } from "Hooks";
/** relative */
import {
  deleteSection,
  updateActiveQuestion,
  deleteSectionV2,
  deleteQuestionV2,
} from "../../modules/WorksheetModules";
import classes from "./DeleteSectionModal.scss";
import {
  MODE_TEACHER_CREATE,
  MODE_TEACHER_VIEW,
  WORKSHEET_MODES,
} from "../../modules/WorksheetConstants";
import { isGlobalHeaderOnMultipleScreensEnabled } from "Utils";
import {
  isWorksheetMutationsV2Enabled,
  isWorksheetQueriesV2Enabled,
} from "../../modules/WorksheetFeatureFlags";

const styles = {
  modalBodyStyle: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "18px",
    padding: "16px 32px 24px",
  },
};

const DeleteSectionModal = props => {
  const {
    id,
    label,
    toggleModal,
    deleteSection,
    updateActiveQuestion,
    mode,
    parentItemId,
    deleteSectionV2,
    deleteQuestionV2,
    isQueriesV2Enabled,
  } = props;

  const { t } = useI18n(["common", "worksheet"]);
  const isGlobalHeaderEnabled = isGlobalHeaderOnMultipleScreensEnabled();

  const removeSection = () => {
    if (mode !== MODE_TEACHER_CREATE) return;

    if (isWorksheetMutationsV2Enabled()) {
      // Use unified V2 function when both mutations and queries are enabled
      const deleteSectionFn = isQueriesV2Enabled
        ? deleteQuestionV2
        : deleteSectionV2;
      deleteSectionFn({ questionId: parentItemId });
    } else {
      deleteSection({ id });
    }
    updateActiveQuestion(null);
    toggleModal();
  };

  return (
    <Modal
      width="480px"
      height="min-content"
      hasOverlay={true}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      isOpen={true}
      onClose={toggleModal}
      message={t("common:delete_msg_with_this_label_lowercase", {
        label: t("worksheet:content_box"),
      })}
      testId="worksheet-deleteSectionModal-closeModal"
    >
      <Modal.Header
        title={t("common:delete_with_quotation_label", {
          label,
        })}
        isClosable={true}
      />
      <Modal.Body style={styles.modalBodyStyle}>
        <div className="text-body-1">
          {t("worksheet:delete_from_worksheet_with_label", {
            label,
          })}
        </div>
        <Alert
          message={t("worksheet:delete_section_warning")}
          type="warning"
          showIcon={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.modalFooter}>
          <Button
            variant="primary"
            type="fill"
            onClick={removeSection}
            testId="worksheet-deleteSectionModal-modalFooter-removeSection"
            dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
            shortcut={{ code: "Enter", shiftKey: true }}
          >
            {t("common:confirm")}
          </Button>
          <Button
            onClick={toggleModal}
            testId="worksheet-deleteSectionModal-modalFooter-cancel"
            variant={isGlobalHeaderEnabled ? "neutral" : "outlined-subtle"}
            type="outlined"
            dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
          >
            {t("common:cancel")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = state => {
  // This is used to determine if the new query structure should be applied
  // V2 queries should be used (feature flag enabled AND not in Toddle Library and not from Internal Tool)
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();
  return {
    isQueriesV2Enabled,
  };
};

const mapActionCreators = {
  deleteSection,
  updateActiveQuestion,
  deleteSectionV2,
  deleteQuestionV2,
};

DeleteSectionModal.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  toggleModal: PropTypes.func,
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  isQueriesV2Enabled: PropTypes.bool,
};

DeleteSectionModal.defaultProps = {
  label: "",
  toggleModal: _.noop,
  mode: MODE_TEACHER_VIEW,
  isQueriesV2Enabled: false,
};

export default connect(mapStateToProps, mapActionCreators)(DeleteSectionModal);
